// DataTable.jsx — Column definitions, cell rendering, and the data table
// Receives computed rows from MarketWatch and renders them
// Uses react-virtuoso TableVirtuoso for virtual scrolling (only renders visible rows)

import React, { forwardRef, useMemo } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { fmtINR, fmtNum } from './calculations';
import CheckboxDropdown from './CheckboxDropdown';
import ResizeGrip from './ResizeGrip';

// ═══════════════════════════════════════════════════════════
// COLUMN DEFINITIONS — Exactly 20 columns per spec
//
//  1. Underlying Name      2. Public/Private     3. Sector
//  4. Option Type (CE/PE)  5. Expiry Date        6. Lot Size
//  7. CMP of Underlying    8. Strike Price       9. Strike/Stock %
// 10. LTP (Option Premium) 10b. Premium Income  11. Margin
// 12. Return %            13. Result Date       14. Annualized Return
// 15. Equal Weights       16. OI                17. Volume
// 18. Best Buy/Sell       19. IV                20. Trade Button
// ═══════════════════════════════════════════════════════════

// Columns with filter: 'xxx' get a compact CheckboxDropdown icon in the header
export const COLUMNS = [
  { key: 'underlying',        label: 'Underlying',     align: 'left',   width: 160 },
  { key: 'publicPrivate',     label: 'Pub/Pvt',        align: 'center', width: 56,  filter: 'publicPrivate' },
  { key: 'sector',            label: 'Sector',         align: 'left',   width: 80,  filter: 'sector' },
  { key: 'category',          label: 'Category',       align: 'left',   width: 80,  filter: 'category' },
  { key: 'type',              label: 'C/P',            align: 'center', width: 36,  filter: 'type' },
  { key: 'expiryStr',         label: 'Expiry',         align: 'left',   width: 80,  filter: 'expiry' },
  { key: 'lotSize',           label: 'Lot',            align: 'right',  width: 44 },
  { key: 'spot',              label: 'CMP',            align: 'right',  width: 70 },
  { key: 'strike',            label: 'Strike',         align: 'right',  width: 70 },
  { key: 'strikeStockRatio',  label: 'S/S%',           align: 'right',  width: 52 },
  { key: 'ltp',               label: 'LTP',            align: 'right',  width: 62 },
  { key: 'ltpCmp',            label: 'Ext/CMP',        align: 'right',  width: 56 },
  { key: 'premiumIncome',     label: 'Premium',        align: 'right',  width: 70 },
  { key: 'margin',            label: 'Margin',         align: 'right',  width: 70 },
  { key: 'returnPct',         label: 'Ret%',           align: 'right',  width: 50 },
  { key: 'resultDate',        label: 'Result',         align: 'left',   width: 80 },
  { key: 'annualized',        label: 'Ann%',           align: 'right',  width: 52 },
  { key: 'equalWeight',       label: 'EW%',            align: 'right',  width: 52 },
  { key: 'oi',                label: 'OI (Lots)',      align: 'right',  width: 70 },
  { key: 'volume',            label: 'Vol (Lots)',     align: 'right',  width: 70 },
  { key: 'bestBuySell',       label: 'Bid/Ask',        align: 'right',  width: 100 },
  { key: 'iv',                label: 'IV%',            align: 'right',  width: 48 },
  // 20 = Trade button (rendered separately, not in columns array)
];

// ═══════════════════════════════════════════════════════════
// RENDER CELL — maps column key to the correct <td>
// ═══════════════════════════════════════════════════════════

// Session 46 — Small pill rendering helper for position/order badges.
// Each badge is "S 100 PE" style. Green for positions, orange for orders.
// Max 2 visible badges then "+N" overflow with a hover tooltip containing the full list.
// Rendered as a row of pills (no leading margin — caller decides placement).
function renderBadges(items, color, bg, border) {
  if (!items || items.length === 0) return null;
  const visible = items.slice(0, 2);
  const overflow = items.length - visible.length;
  const tooltip = items.map(b => {
    const exp = b.expiryStr ? ` ${b.expiryStr}` : '';
    const acc = b.accountLabel ? ` (${b.accountLabel})` : '';
    return `${b.direction} ${b.strike || '?'} ${b.type || ''}${exp}${acc}`;
  }).join('\n');
  const pillStyle = {
    fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3,
    background: bg, color: color, border: `1px solid ${border}`,
    whiteSpace: 'nowrap', lineHeight: 1.2, flexShrink: 0
  };
  return (
    <span style={{ display: 'inline-flex', gap: 3, flexShrink: 0 }} title={tooltip}>
      {visible.map((b, i) => (
        <span key={i} style={pillStyle}>
          {b.direction}{' '}{b.strike || '?'}{' '}{b.type || ''}
        </span>
      ))}
      {overflow > 0 && (
        <span style={pillStyle}>+{overflow}</span>
      )}
    </span>
  );
}

function renderCell(col, row, t, isDark, rowPad, fontSize, extras) {
  const ex = extras || {};
  const liveTokens = ex.liveTokens; // Set<number> or undefined
  const positionsBySymbol = ex.positionsBySymbol; // Map<symbol, badges[]> or undefined
  const ordersBySymbol = ex.ordersBySymbol; // Map<symbol, badges[]> or undefined

  switch (col.key) {
    case 'underlying': {
      // Session 46 — Underlying name on top, badges BELOW (stacked).
      // Name always visible at full width. Badges row appears only if any exist.
      // Match by row.symbol (NSE ticker, e.g. "RELIANCE"), uppercased.
      const symKey = (row.symbol || '').toUpperCase();
      const posBadges = positionsBySymbol ? positionsBySymbol.get(symKey) : null;
      const ordBadges = ordersBySymbol ? ordersBySymbol.get(symKey) : null;
      const hasBadges = (posBadges && posBadges.length > 0) || (ordBadges && ordBadges.length > 0);
      return (
        <td key={col.key} style={{
          padding: rowPad, fontWeight: 600, color: t.text, maxWidth: col.width, verticalAlign: 'top'
        }} title={row.underlying}>
          <div style={{
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%'
          }}>{row.underlying}</div>
          {hasBadges && (
            <div style={{ marginTop: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              {renderBadges(posBadges, isDark ? '#86efac' : '#16a34a',
                isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)',
                'rgba(34,197,94,0.35)')}
              {renderBadges(ordBadges, isDark ? '#fdba74' : '#ea580c',
                isDark ? 'rgba(249,115,22,0.15)' : 'rgba(249,115,22,0.12)',
                'rgba(249,115,22,0.35)')}
            </div>
          )}
        </td>
      );
    }
    case 'publicPrivate':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'center', color: t.textMuted, fontSize: fontSize - 1 }}>{row.publicPrivate || '—'}</td>;
    case 'sector':
      return <td key={col.key} style={{ padding: rowPad, color: t.textMuted, fontSize: fontSize - 1 }}>{row.sector || '—'}</td>;
    case 'category':
      return <td key={col.key} style={{ padding: rowPad, color: t.textMuted, fontSize: fontSize - 1 }}>{row.category || '—'}</td>;
    case 'ltpCmp': {
      const val = row.ltpCmp;
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.textMuted }}>{val > 0 ? `${val}%` : '—'}</td>;
    }
    case 'type':
      return (
        <td key={col.key} style={{ padding: rowPad, textAlign: 'center' }}>
          <span style={{
            fontSize: fontSize - 2, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
            background: row.type === 'CE' ? (isDark ? '#1a2e1a' : '#dcfce7') : (isDark ? '#2e1a1a' : '#fee2e2'),
            color: row.type === 'CE' ? t.green : t.red
          }}>{row.type}</span>
        </td>
      );
    case 'expiryStr':
      return <td key={col.key} style={{ padding: rowPad, color: t.textMuted, fontSize: fontSize - 1 }}>{row.expiryStr}</td>;
    case 'lotSize':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.textMuted }}>{row.lotSize}</td>;
    case 'spot':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.textMuted }}>{row.spot > 0 ? Number(row.spot).toFixed(2) : '—'}</td>;
    case 'strike':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', fontWeight: 500 }}>{fmtNum(row.strike)}</td>;
    case 'strikeStockRatio':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.textMuted }}>{`${Number(row.strikeStockRatio).toFixed(2)}%`}</td>;
    case 'ltp': {
      // Session 46 — WS indicator dot.
      // Filled green dot = token subscribed to Kite WebSocket (real-time, <1s latency).
      // Hollow grey circle = token on 30-second REST polling (data can be up to 30s stale).
      // O(1) Set lookup. Set updates only when smart promotion fires (rare), so no
      // re-render cost on every tick.
      const isLive = liveTokens && row.token && liveTokens.has(row.token);
      return (
        <td key={col.key} style={{ padding: rowPad, textAlign: 'right', fontWeight: 600, color: t.text }}>
          <span
            title={isLive ? 'Live (WebSocket — real-time)' : 'Polled (REST — up to 30s lag)'}
            style={{
              display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
              marginRight: 5, verticalAlign: 'middle', flexShrink: 0,
              background: isLive ? '#22c55e' : 'transparent',
              border: isLive ? '1px solid #22c55e' : `1px solid ${isDark ? '#475569' : '#94a3b8'}`
            }}
          />
          {`₹${Number(row.ltp).toFixed(2)}`}
        </td>
      );
    }
    case 'premiumIncome':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.green }}>{fmtINR(row.premiumIncome)}</td>;
    case 'margin':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.textMuted }}>{fmtINR(row.margin)}</td>;
    case 'returnPct':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', fontWeight: 600, color: row.returnPct > 2 ? t.yellow : t.text }}>{`${Number(row.returnPct).toFixed(2)}%`}</td>;
    case 'resultDate':
      return (
        <td key={col.key} style={{ padding: rowPad, fontSize: fontSize - 1 }}>
          {row.resultDate ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {row.resultsBeforeExpiry && (
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: t.red, flexShrink: 0 }} title="Results before expiry" />
              )}
              <span style={{ color: row.isFutureResult ? t.yellow : t.textMuted }}>{row.resultDate}</span>
            </span>
          ) : (
            <span style={{ color: t.textDim }}>—</span>
          )}
        </td>
      );
    case 'annualized':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: row.annualized > 30 ? t.yellow : t.textMuted }}>{`${Number(row.annualized).toFixed(2)}%`}</td>;
    case 'equalWeight':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.textMuted }}>{`${Number(row.equalWeight).toFixed(2)}%`}</td>;
    case 'oi':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.textMuted, fontSize: fontSize - 1 }}>{fmtNum(row.oi)}</td>;
    case 'volume':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.textMuted, fontSize: fontSize - 1 }}>{fmtNum(row.volume)}</td>;
    case 'bestBuySell':
      return (
        <td key={col.key} style={{ padding: rowPad, textAlign: 'right', fontSize: fontSize - 1 }}>
          {(row.bestBuyPrice > 0 || row.bestSellPrice > 0) ? (
            <span>
              <span style={{ color: t.green }}>{row.bestBuyPrice > 0 ? Number(row.bestBuyPrice).toFixed(2) : '—'}</span>
              <span style={{ color: t.textDim }}> / </span>
              <span style={{ color: t.red }}>{row.bestSellPrice > 0 ? Number(row.bestSellPrice).toFixed(2) : '—'}</span>
            </span>
          ) : '—'}
        </td>
      );
    case 'iv':
      return <td key={col.key} style={{ padding: rowPad, textAlign: 'right', color: t.purple }}>{`${Number(row.iv).toFixed(2)}%`}</td>;
    default:
      return <td key={col.key} style={{ padding: rowPad }}>—</td>;
  }
}

// ═══════════════════════════════════════════════════════════
// DATA TABLE COMPONENT — Virtual Scrolling via TableVirtuoso
// ═══════════════════════════════════════════════════════════

function DataTable({
  themeCtx, displayRows, rows,
  sortCol, sortDir, onSort, onOpenOrderPopup,
  filters = {},
  // Session 46 — props for WS indicator + position/order badges (all optional;
  // DataTable degrades gracefully if any are absent so the same component still
  // renders cleanly in demo mode or before SSE arrives).
  liveTokens,
  positionsBySymbol,
  ordersBySymbol,
  // Per-user column layout (order/width/hidden) from useColumnLayout — defaults to the
  // full base list so DataTable renders identically when the props are absent.
  columns = COLUMNS,
  onColumnResize
}) {
  const { t, isDark, rowPad, fontSize } = themeCtx;

  // Bundle the extras into a single object so renderCell stays cheap to call.
  // useMemo'd so the object identity is stable when the underlying state hasn't changed
  // — important because renderCell is called per-row per-render (~hundreds of times).
  const extras = useMemo(() => ({
    liveTokens, positionsBySymbol, ordersBySymbol
  }), [liveTokens, positionsBySymbol, ordersBySymbol]);

  // Map filter keys to their state/options (passed from MarketWatch)
  const filterMap = {
    expiry:        { selected: filters.expiryFilter        || [], setSelected: filters.setExpiryFilter,        options: filters.uniqueExpiries        || [] },
    type:          { selected: filters.typeFilter          || [], setSelected: filters.setTypeFilter,          options: ['All', 'CE', 'PE'] },
    publicPrivate: { selected: filters.publicPrivateFilter || [], setSelected: filters.setPublicPrivateFilter, options: filters.uniquePublicPrivate   || [] },
    sector:        { selected: filters.sectorFilter        || [], setSelected: filters.setSectorFilter,        options: filters.uniqueSectors         || [] },
    category:      { selected: filters.categoryFilter      || [], setSelected: filters.setCategoryFilter,      options: filters.uniqueCategories      || [] },
  };

  // Custom components for Virtuoso's table structure
  // Docs: "border-collapse: collapse causes fixed header borders to scroll away. Use border-collapse: separate."
  const tableComponents = useMemo(() => ({
    Table: forwardRef(({ style, ...props }, ref) => (
      <>
        <style>{`table.vt tbody td { border-bottom: 1px solid ${t.border}; }`}</style>
        <table
          ref={ref}
          {...props}
          className="vt"
          style={{ ...style, width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize, minWidth: 1200 }}
        />
      </>
    )),
    TableRow: forwardRef(({ style, ...props }, ref) => (
      <tr
        ref={ref}
        {...props}
        style={{ ...style, background: t.bgRow }}
        onMouseEnter={e => { e.currentTarget.style.background = t.bgRowHover; }}
        onMouseLeave={e => { e.currentTarget.style.background = t.bgRow; }}
      />
    )),
  }), [fontSize, t.border, t.bgRow, t.bgRowHover]);

  // If no rows, show empty state (Virtuoso won't render anything useful with 0 items)
  if (rows.length === 0) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 40, textAlign: 'center', color: t.textMuted }}>
          {displayRows === rows && rows.length === 0 && !window.__sseSnapshotReceived
            ? 'Loading market data...'
            : 'No options match your filters'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowX: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <TableVirtuoso
        style={{ flex: 1, minHeight: 0 }}
        data={displayRows}
        // Track rows by stable identity (token), NOT array index. Without this, virtuoso re-keys by
        // position on every per-second data flush — so when a row leaves the filtered set above your
        // viewport (a tick crossing OI/Vol/S-S), the content under your scroll position shifts and you
        // lose the row you were watching. Keying by id makes virtuoso hold the visible rows in place.
        computeItemKey={(_, row) => (row.id != null ? row.id : row.token)}
        components={tableComponents}
        overscan={20}
        fixedHeaderContent={() => (
          <tr style={{ background: t.headerBg }}>
            {columns.map(col => {
              const fm = col.filter ? filterMap[col.filter] : null;
              return (
                <th
                  key={col.key}
                  style={{
                    padding: rowPad, textAlign: col.align, fontWeight: 600, fontSize: fontSize - 1.5,
                    color: t.headerText, textTransform: 'uppercase', letterSpacing: 0.3,
                    cursor: 'pointer', userSelect: 'none', position: 'relative', borderBottom: `1px solid ${t.borderHeader}`,
                    whiteSpace: 'nowrap', width: col.width, minWidth: col.width,
                    background: sortCol === col.key ? (isDark ? '#1e2240' : '#e8ecff') : t.headerBg
                  }}
                >
                  <span onClick={() => onSort(col.key)}>{col.label}</span>
                  {sortCol === col.key && <span style={{ marginLeft: 2, fontSize: 9 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                  {fm && fm.setSelected && (
                    <CheckboxDropdown
                      label={col.label}
                      selected={fm.selected}
                      setSelected={fm.setSelected}
                      options={fm.options}
                      t={t}
                      compact
                    />
                  )}
                  {onColumnResize && <ResizeGrip colKey={col.key} width={col.width} onResize={onColumnResize} />}
                </th>
              );
            })}
            <th style={{ padding: rowPad, width: 52, minWidth: 52, borderBottom: `1px solid ${t.borderHeader}`, background: t.headerBg }} />
          </tr>
        )}
        itemContent={(index, row) => (
          <>
            {columns.map(col => renderCell(col, row, t, isDark, rowPad, fontSize, extras))}
            <td style={{ padding: rowPad, textAlign: 'center' }}>
              <button
                onClick={() => onOpenOrderPopup(row)}
                style={{
                  padding: '2px 10px', fontSize: fontSize - 1, fontWeight: 600,
                  background: isDark ? '#1e2240' : '#e8ecff',
                  color: t.blue, border: 'none', borderRadius: 3, cursor: 'pointer'
                }}
              >
                Trade
              </button>
            </td>
          </>
        )}

      />
    </div>
  );
}

export default DataTable;
